import type { DefaultRootState } from "react-redux";
import { getAnvilWidgetDOMId } from "layoutSystems/common/utils/LayoutElementPositionsObserver/utils";
import { LayoutSystemTypes } from "layoutSystems/types";
import { selectFeatureFlagCheck } from "ee/selectors/featureFlagsSelectors";
import { FEATURE_FLAG } from "ee/entities/FeatureFlag";

/**
 * The Anvil isolation boundary (M0).
 *
 * An application persisted as ANVIL only resolves to the Anvil layout system
 * when an Anvil-enabling flag is on:
 * - `license_anvil_enabled` — the sole product entitlement gate (Decision 10);
 *   cloud-services drops it when the license lapses/downgrades.
 * - `release_anvil_enabled` — RETIRED (Decision 10; no longer in the server
 *   FeatureFlagEnum, so never served). Kept in the OR only so local
 *   `window.overrideFeatureFlag` workflows and legacy Cypress fixtures keep
 *   working until the full reference sweep removes it everywhere at once
 *   ("sequence this so Anvil isn't briefly ungated during the swap").
 *
 * The `license_ai_agent_enabled` carve-out was REMOVED (2026-07-06): AI agents
 * are no longer a supported product, so there are no AI-Agent apps
 * (ANVIL-typed without the Anvil entitlement) left to preserve — the license
 * flag is now the sole Anvil-enabling predicate across client and server.
 *
 * When no enabling flag is on, an ANVIL-typed app falls back to FIXED at the
 * layout-resolution level. NOTE (known layering gap, tracked in
 * .claude/anvil/risks.md): `getIsAnvilAppLicenseLocked` currently derives from
 * this gated selector rather than the persisted `appPositioning.type`, so the
 * `AnvilLicenseGate` block UX does not fire for unlicensed pure-Anvil apps
 * (they fall through to this FIXED fallback; the server 403 is the effective
 * guard). Re-keying the lock off the persisted type is a tracked follow-up —
 * do not "fix" it here by changing this OR.
 *
 * This check is only consulted for ANVIL-typed apps — FIXED/AUTO apps never
 * read the flags and are therefore behaviourally unchanged.
 */
const isAnvilEnabled = (state: DefaultRootState) =>
  selectFeatureFlagCheck(state, FEATURE_FLAG.license_anvil_enabled) ||
  selectFeatureFlagCheck(state, FEATURE_FLAG.release_anvil_enabled);

/**
 * Returns the layout system type based on the state of the application.
 * @param state - The current state of the application.
 * @returns The layout system type.
 */
export const getLayoutSystemType = (state: DefaultRootState) => {
  const applicationLayoutSystemType =
    state.ui.applications?.currentApplication?.applicationDetail?.appPositioning
      ?.type;

  // Check if the application has a defined appPositioning type
  if (applicationLayoutSystemType) {
    // Get the layout system type based on the appPositioning type
    const layoutSystemType = LayoutSystemTypes[applicationLayoutSystemType];

    // Anvil isolation boundary: gate the ANVIL branch behind the Anvil flag(s).
    // Any other persisted type (FIXED/AUTO) is returned unchanged.
    if (
      layoutSystemType === LayoutSystemTypes.ANVIL &&
      !isAnvilEnabled(state)
    ) {
      return LayoutSystemTypes.FIXED;
    }

    return layoutSystemType;
  }

  // If no layout system type is found, return FIXED as the default layout system type
  return LayoutSystemTypes.FIXED;
};

export const getWidgetSelectorByWidgetId = (
  state: DefaultRootState,
  widgetId: string,
) => {
  const layoutSystemType = getLayoutSystemType(state);

  switch (layoutSystemType) {
    case LayoutSystemTypes.ANVIL:
      return getAnvilWidgetDOMId(widgetId);
    default:
      return widgetId;
  }
};

export const isFixedLayoutSelector = (state: DefaultRootState) =>
  getLayoutSystemType(state) === LayoutSystemTypes.FIXED;
